import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { formatPrice, hasSplitPayment, formatSplitPaymentInfo } from "../utils/formatters";

const PaymentChoiceDialog = ({
  open,
  onClose,
  onPayOnline,
  onPayOnSite,
  paymentOnlyOnline = false,
  lessonData,
  // Group bookings: total the booker pays (own seat + friend seats) and a
  // caption for it; default is the single lesson price.
  amount = null,
  amountLabel = "Koszt zajęć",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isSplitPayment = hasSplitPayment(lessonData);
  const splitInfo = isSplitPayment ? formatSplitPaymentInfo(lessonData.online_payment_amount, lessonData.on_site_payment_amount) : null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth 
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 2,
          m: isMobile ? 2 : 3,
        }
      }}
    >
      <DialogTitle sx={{ textAlign: 'center', pt: 3, pb: 2 }}>
        <Typography variant={isMobile ? "h6" : "h5"} fontWeight="600">
          {isSplitPayment 
            ? "💳 Płatność podzielona"
            : paymentOnlyOnline 
              ? "💳 Płatność online wymagana"
              : "💳 Wybierz formę płatności"
          }
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 2, pb: 3 }}>
        {isSplitPayment ? (
          <Stack spacing={2.5}>
            <Alert severity="info" icon="ℹ️" sx={{ borderRadius: 2 }}>
              <Typography variant="body2" fontWeight="500">
                Te zajęcia wymagają płatności podzielonej
              </Typography>
            </Alert>
            
            <Card elevation={0} sx={{ backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
              <CardContent>
                <Stack spacing={2}>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        💳 Płatność online (teraz)
                      </Typography>
                      <Chip 
                        label={splitInfo.online} 
                        color="primary" 
                        sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}
                      />
                    </Stack>
                  </Box>
                  
                  <Divider />
                  
                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        💵 Płatność na miejscu
                      </Typography>
                      <Chip 
                        label={splitInfo.onsite} 
                        variant="outlined"
                        sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}
                      />
                    </Stack>
                  </Box>
                  
                  <Divider />
                  
                  <Box sx={{ backgroundColor: '#e3f2fd', p: 2, borderRadius: 1, mt: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body1" fontWeight="600">
                        Razem
                      </Typography>
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        {splitInfo.total}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
            
            <Alert severity="warning" icon="⚠️" sx={{ borderRadius: 2 }}>
              <Typography variant="caption" fontWeight="600" gutterBottom>
                UWAGA: Płatność online jest zadatkiem i nie podlega zwrotowi!
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                Zapłać teraz <strong>{splitInfo.online}</strong> online (zadatek). Pozostałą kwotę <strong>{splitInfo.onsite}</strong> uiścisz na miejscu.
              </Typography>
            </Alert>
            
            <Alert severity="error" icon="🚫" sx={{ borderRadius: 2 }}>
              <Typography variant="caption" fontWeight="500">
                Po zapisaniu nie będzie możliwości wypisania się z tych zajęć.
              </Typography>
            </Alert>
          </Stack>
        ) : (
          <Stack spacing={2}>
            {paymentOnlyOnline && (
              <Alert severity="warning" icon="⚠️" sx={{ borderRadius: 2 }}>
                <Typography variant="body2">
                  Te zajęcia wymagają płatności online. Masz <strong>15 minut</strong> na dokonanie płatności.
                </Typography>
              </Alert>
            )}
            
            {(amount || lessonData?.lesson_type?.price) && (
              <Card elevation={0} sx={{ backgroundColor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {amountLabel}
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary" data-testid="payment-amount">
                    {formatPrice(amount || lessonData.lesson_type.price)}
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Stack>
        )}
      </DialogContent>
      
      <DialogActions sx={{ 
        px: 3, 
        pb: 3, 
        gap: 1,
        flexDirection: isMobile ? 'column' : 'row'
      }}>
        <Button
          onClick={onPayOnline}
          color="success"
          variant="contained"
          fullWidth={isMobile}
          sx={{ minHeight: 44 }}
        >
          {isSplitPayment ? `Zapłać ${splitInfo.online} online` : 'Zapłać online'}
        </Button>
        {!paymentOnlyOnline && !isSplitPayment && (
          <Button 
            onClick={onPayOnSite} 
            color="success" 
            variant="outlined"
            fullWidth={isMobile}
            sx={{ minHeight: 44 }}
          >
            Zapłać na miejscu
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PaymentChoiceDialog;
